import { Link, QuoteShareLink } from '@setvi/shared/interfaces';
import { useCallback } from 'react';
import { List } from './list';
import { EditLinks } from './edit-links';
import { QuoteLinkEdit } from './quote-link-edit';

interface ReviewLinksProps {
  link: Link;
  links: Link[];
  /** The email's quote share links, if the share flow inserted any (DEV-785). */
  quoteLinks?: QuoteShareLink[];
  /**
   * Which quote link's rename view is open, if any. Owned by the caller, like
   * `link` is, so a link's own Edit button in the email can open it too.
   */
  openQuoteLinkIndex?: number | null;
  handleEdit: (link: Link) => void;
  handleEditQuoteLink?: (index: number) => void;
  handleBack?: () => void;
  handleOpen?: () => void;
  handleClose: () => void;
  handleInsert(link: Link, currentLink?: Link): void;
  handleRemoveLink(link?: Link): void;
}

export const ReviewLinks = ({
  link,
  links,
  quoteLinks,
  openQuoteLinkIndex,
  handleBack,
  handleEdit,
  handleEditQuoteLink,
  handleOpen,
  handleClose,
  handleInsert,
  handleRemoveLink
}: ReviewLinksProps) => {
  const openQuoteLink =
    typeof openQuoteLinkIndex === 'number'
      ? quoteLinks?.[openQuoteLinkIndex]
      : undefined;

  const handleRemove = useCallback(
    (removeLink?: Link) => {
      // If this is the last link left in the email
      if (links.length < 2 && !quoteLinks?.length) {
        handleRemoveLink(removeLink);
        handleClose();
      }
      handleRemoveLink(removeLink);
    },
    [handleRemoveLink, handleClose, links, quoteLinks]
  );

  const handleRemoveQuoteLink = useCallback(
    (removeLink: QuoteShareLink) => {
      removeLink?.remove();
      handleBack?.();
      // Nothing left to review once it was the email's only link.
      if (!links?.length && (quoteLinks?.length || 0) < 2) handleClose();
    },
    [links, quoteLinks, handleBack, handleClose]
  );

  if (openQuoteLink)
    return (
      <QuoteLinkEdit
        quoteLink={openQuoteLink}
        handleBack={handleBack}
        handleClose={handleClose}
        handleDelete={() => handleRemoveQuoteLink(openQuoteLink)}
      />
    );

  if (link)
    return (
      <EditLinks
        link={link}
        handleBack={handleBack}
        handleInsert={handleInsert}
        handleRemoveLink={handleRemove}
        handleClose={handleClose}
        handleOpen={handleOpen}
      />
    );

  return (
    <List
      links={links}
      quoteLinks={quoteLinks}
      handleEdit={handleEdit}
      handleEditQuoteLink={handleEditQuoteLink}
      handleRemoveLink={handleRemove}
      handleRemoveQuoteLink={handleRemoveQuoteLink}
      handleClose={handleClose}
    />
  );
};
